import { Checkbox } from '@components/Checkbox/Checkbox';
import { FoldHeader } from '@components/FoldHeader/FoldHeader';
import { SocialAccountAvatar } from '@components/SocialAccountAvatar/SocialAccountAvatar';
import { ApiError } from '@lib/network/api';
import {
  getSocialPlatformModule,
  type AnySocialPlatformModule,
} from '@lib/social/scheduling/platforms';
import {
  describeRefusalCode,
  describeSocialApiError,
  describeViolation,
} from '@lib/social/scheduling/posts';
import formCss from '@lib/style/form.module.scss';
import { PUBLISHING_SETTINGS_PATH } from '@lib/utils/socialConnectReturn';
import { WarningAmberRounded } from '@mui/icons-material';
import { ButtonBase } from '@mui/material';
import { socialCapabilitiesStore } from '@stores/SocialCapabilities';
import type { SocialConnection } from '@stores/SocialConnections';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import css from './AccountCard.module.scss';
import { usePostForm } from './PostFormStore';
import { PriorPostsLine } from './PriorPostsLine';

/** The account's live settings under its name: what it may post, or why it cannot. */
const CapabilitiesLine = observer(function CapabilitiesLine({
  connection,
  module,
}: {
  connection: SocialConnection;
  module: AnySocialPlatformModule;
}) {
  const state = socialCapabilitiesStore.state(connection.id);
  if (!state || state.fetching) return <span className={css.meta}>Reading account settings…</span>;
  if (state.error) {
    return (
      <span className={classNames(css.meta, css.warn)}>
        {describeSocialApiError(state.error) ??
          'Could not read the account’s settings; the platform’s defaults are offered instead.'}{' '}
        {!(
          state.error instanceof ApiError && state.error.code === 'social.connection.needs_reauth'
        ) && (
          <ButtonBase
            className={css.inlineAction}
            onClick={(e) => {
              // Nested in the foldable header: the click is the retry's alone.
              e.stopPropagation();
              void socialCapabilitiesStore.load(connection);
            }}
          >
            Retry
          </ButtonBase>
        )}
      </span>
    );
  }
  const capabilities = state.capabilities;
  if (!capabilities) return null;
  if (!capabilities.available) {
    return (
      <span className={classNames(css.meta, css.error)}>
        {describeRefusalCode(capabilities.code) ?? 'This account cannot post right now.'}
      </span>
    );
  }
  return <span className={css.meta}>{module.describeCapabilities(capabilities)}</span>;
});

/**
 * One account in the form. On create it is selectable and, once selected,
 * opens its platform's fields; on edit it is the post's account with the
 * fields open on the stored content. What the render already did on the
 * account sits under its name; the last refusal for the account sits under
 * the card.
 */
export const AccountCard = observer(function AccountCard({
  connection,
  selectable,
}: {
  connection: SocialConnection;
  /** Create: a checkbox picks the account. Edit: the account is fixed. */
  selectable: boolean;
}) {
  const form = usePostForm();
  const module = getSocialPlatformModule(connection.platform);
  const name = connection.displayName ?? module?.label ?? connection.platform;
  const needsReauth = connection.status === 'needs_reauth';
  const selected = form.selected.has(connection.id);
  const content = form.contents.get(connection.id);
  const violations = form.violations.get(connection.id);
  const canPick = selectable && form.canPick(connection);
  // Folded by the header; the checkbox alone changes the selection, so a
  // click meant to tidy the page never drops an account or its content.
  const [collapsed, setCollapsed] = useState(false);
  const open = selected && module !== undefined && content !== undefined;
  // The fields take the account's settings; a verdict on the account is the blocker's to word, not theirs.
  const capabilities = socialCapabilitiesStore.capabilities(connection.id);
  const available = capabilities?.available ? capabilities : null;

  return (
    <div
      className={classNames(
        css.card,
        selected && css.selected,
        !canPick && selectable && css.unavailable,
      )}
    >
      <FoldHeader
        foldable={open}
        collapsed={collapsed}
        onToggle={() => setCollapsed((c) => !c)}
        className={css.header}
      >
        {selectable && (
          <Checkbox
            checked={selected}
            disabled={!canPick}
            onChange={() => form.toggleAccount(connection.id)}
            onClick={(e) => e.stopPropagation()}
            className={css.checkbox}
          />
        )}
        <SocialAccountAvatar connection={connection} />
        <div className={css.info}>
          <span className={css.name}>{name}</span>
          {!module ? (
            <span className={classNames(css.meta, css.warn)}>
              Unsupported platform: {connection.platform}
            </span>
          ) : needsReauth ? (
            <span className={classNames(css.meta, css.warn)}>
              <WarningAmberRounded /> Reconnect needed ·{' '}
              <Link to={PUBLISHING_SETTINGS_PATH} className={css.inlineAction}>
                Open settings
              </Link>
            </span>
          ) : (
            (selected || !selectable) && (
              <CapabilitiesLine connection={connection} module={module} />
            )
          )}
          <PriorPostsLine connection={connection} name={name} />
        </div>
      </FoldHeader>

      {open && !collapsed && (
        <div className={css.body}>
          <module.ComposeFields
            content={content}
            onChange={(next) => form.setContent(connection.id, next)}
            capabilities={available}
            disabled={form.readOnly}
          />
        </div>
      )}

      {violations && (
        <div className={classNames(formCss.warningBox, css.violations)} role="alert">
          <WarningAmberRounded className={formCss.warningIcon} />
          <div className={formCss.warningBody}>
            <span className={formCss.warningTitle}>
              {module?.label ?? connection.platform} refused this post
            </span>
            {violations.length === 0 ? (
              <span>No reason was given.</span>
            ) : (
              violations.map((violation, i) => <span key={i}>{describeViolation(violation)}</span>)
            )}
          </div>
        </div>
      )}
    </div>
  );
});
